import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import { TreeNode } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { TreeSelectModule } from 'primeng/treeselect';

import { ProductDetailsGroupBys } from '@core/constants/report';
import { IdName } from '@core/models/interfaces/common';
import { Company } from '@core/models/interfaces/company';
import { Product } from '@core/models/interfaces/product';
import { ProductType } from '@core/models/interfaces/product-type';
import { ProductDetailsReportParams } from '@core/models/interfaces/report';
import { UserDetail } from '@core/models/interfaces/user';
import { ButtonComponent } from '@shared/components/button/button.component';
import { CustomCalendarComponent } from '@shared/components/custom-calendar/custom-calendar.component';
import { FormControlComponent } from '@shared/components/form-control/form-control.component';
import { UserFormDropdownComponent } from '@shared/components/dropdown/user-form-dropdown/user-form-dropdown.component';
import { ReportField, requiredExportFieldsProductionDetailsKey } from '@core/models/interfaces/report-field';
import { ReportService } from '@core/services/report.service';
import { Subject, takeUntil } from 'rxjs';
import { ReportFieldSelectionComponent } from '@shared/components/report-field-selection/report-field-selection.component';

@Component({
  selector: 'app-production-details-selection',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MultiSelectModule,
    CalendarModule,
    FormsModule,
    CommonModule,
    TreeSelectModule,
    TranslateModule,
    DropdownModule,
    FormControlComponent,
    ButtonComponent,
    CustomCalendarComponent,
    UserFormDropdownComponent,
    ReportFieldSelectionComponent
  ],
  templateUrl: './production-details-selection.component.html',
  styleUrl: './production-details-selection.component.scss'
})
export class ProductionDetailsSelectionComponent implements OnInit, OnChanges {
  @Input() isRunning: boolean = false;
  @Input() params: ProductDetailsReportParams;
  @Input() commissionTeams: IdName[] = [];
  @Input() companies: Company[] = [];
  @Input() productTypes: ProductType[] = [];
  @Input() products: Product[] = [];
  @Input() sources: TreeNode[] = [];
  @Input() policyStatuses: TreeNode[] = [];

  @Output() paramsChange = new EventEmitter<ProductDetailsReportParams>();
  @Output() isRunningChange = new EventEmitter<boolean>();

  readonly translatePrefix = 'report.';
  readonly ProductDetailsGroupBys = ProductDetailsGroupBys;

  private unsubscribe$ = new Subject<void>();
  exportSelectedFields: ReportField[] = []
  isSelectionDialogVisible: boolean = false;
  isAllExportFieldsSelected: boolean = true;

  productionDetailsForm = this.fb.group({
    // OBJECTIVE SELECTION
    commissionTeams: this.fb.control<string[]>(['']),
    // FIELD SELECTION
    sources: this.fb.control<TreeNode[]>([{ label: '' }]),
    // FIELD SELECTION
    policyStatuses: this.fb.control<TreeNode[]>([{ label: '' }]),
    companies: this.fb.control<string[]>(['']),
    productTypes: this.fb.control<string[]>(['']),
    productNames: this.fb.control<string[]>(['']),
    commissionOwners: this.fb.control<UserDetail[]>([]),
    commissionAgents: this.fb.control<UserDetail[]>([]),
    groupBy: this.fb.control<string | null>(null, [Validators.required]),
  });

  createdDates: Array<Date | undefined> = [];
  effectiveDates: Array<Date | undefined> = [];

  constructor(
    private fb: FormBuilder,
    private reportService: ReportService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.fetchExportSelectedFields();
    // Khi component khởi tạo lần đầu, nếu đã có params (VD: load từ URL),
    // đồng bộ form + date từ params.
    if (this.params) {
      this.syncFormFromParams(this.params);
    }
  }

  /**
   * Lắng nghe thay đổi @Input params (VD: sau khi ReportComponent map từ queryParams).
   * Giúp dateTime selector + groupBy hiển thị đúng giá trị từ URL.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['params']?.currentValue) {
      this.syncFormFromParams(changes['params'].currentValue as ProductDetailsReportParams);
    }
  }

  /**
   * Đồng bộ form controls, mảng ngày và exportSelectedFields từ ProductDetailsReportParams.
   * Dùng cho cả ngOnInit và ngOnChanges (khi back về từ page khác, params từ URL được map lại).
   */
  private syncFormFromParams(params: ProductDetailsReportParams) {
    // Trim fullName để tránh khoảng trắng thừa khi load từ URL.
    const sanitizeUsers = (users: any[] | undefined) =>
      users?.map(u => ({ ...u, fullName: u.fullName?.trim() || '' })) || [];

    this.productionDetailsForm.patchValue({
      commissionTeams: params.commissionTeamIds,
      sources: params.leadSources as TreeNode[],
      policyStatuses: params.statuses as TreeNode[],
      companies: params.companyIds,
      productTypes: params.productTypeIds,
      productNames: params.productIds,
      commissionOwners: sanitizeUsers(params.commissionOwners),
      commissionAgents: sanitizeUsers(params.commissionAgents),
      groupBy: params.groupBy,
    });
    this.createdDates = [
      params.createdFrom ? new Date(params.createdFrom) : undefined,
      params.createdTo ? new Date(params.createdTo) : undefined
    ].filter(item => !!item);
    this.effectiveDates = [
      params.effectiveFrom ? new Date(params.effectiveFrom) : undefined,
      params.effectiveTo ? new Date(params.effectiveTo) : undefined
    ].filter(item => !!item);

    // Đồng bộ exportSelectedFields từ params.visibleFields (khi back về từ page khác, params đã có từ URL).
    if (params.visibleFields?.length && this.exportSelectedFields?.length) {
      const selectedFromParams = params.visibleFields;
      this.exportSelectedFields.forEach(e => {
        e.isSelected = !!selectedFromParams?.find(f => f === e.key);
      });
      this.onExportFieldChanged();
    }
  }

  get requiredExportFieldsProductionDetailsKey() {
    return requiredExportFieldsProductionDetailsKey;
  }

  fetchExportSelectedFields() {
    return this.reportService
      .getProductionReportFields()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (result) => {
          this.exportSelectedFields = result.data as ReportField[]
          if (this.params) {
            const selectedFromParams = this.params.visibleFields;
            this.exportSelectedFields.forEach(e => {
              if (selectedFromParams?.find(f => f === e.key)) {
                e.isSelected = true;
              } else {
                e.isSelected = false
              }
            })
          } else {
            this.exportSelectedFields.forEach(e => {
              e.isSelected = true;
            })
          }
          this.onExportFieldChanged()
        },
        error: () => {}
      })
  }

  get productTypeOptions() {
    const companyIds = this.productionDetailsForm.value.companies?.filter(item => !!item) as string[];

    if (companyIds?.length) {
      return this.productTypes.filter(item => companyIds.includes(item.company?.id));
    } else {
      return this.productTypes;
    }
  }

  get productOptions() {
    const productTypeIds = this.productionDetailsForm.value.productTypes?.filter(item => !!item) as string[];

    if (productTypeIds?.length) {
      return this.products.filter(item => productTypeIds.includes(item.productType?.id));
    } else {
      return this.products;
    }
  }

  getCommissionTeamsValue(value: IdName[]) {
    return value?.map(item => item.name).join(', ');
  }

  getTreeNodeValue(value: TreeNode[]) {
    return value
      ?.filter(item => !!item.label)
      ?.map(item => item.label)
      .join(', ');
  }

  getCompaniesValue(value: Company[]) {
    return value?.map(item => item.name).join(', ');
  }

  getProductTypesValue(value: ProductType[]) {
    return value?.map(item => item.name).join(', ');
  }

  getProductsValue(value: Product[]) {
    return value?.map(item => item.name).join(', ');
  }

  onChangeCompany() {
    this.productionDetailsForm.patchValue({
      productTypes: [],
      productNames: []
    });
  }

  onChangeProductType() {
    this.productionDetailsForm.patchValue({
      productNames: []
    });
  }

  onReset() {
    this.productionDetailsForm.patchValue({
      // OBJECTIVE SELECTION
      commissionTeams: [''],
      // FIELD SELECTION
      sources: [{ label: '' }],
      // FIELD SELECTION
      policyStatuses: [{ label: '' }],
      companies: [''],
      productTypes: [''],
      productNames: [''],
      commissionOwners: [],
      commissionAgents: [],
      groupBy: null
      // DATE
    });
    this.createdDates = [];
    this.effectiveDates = [];
    this.isAllExportFieldsSelected = true;
    this.exportSelectedFields.forEach(e => e.isSelected = true)
    this.onExportFieldChanged()
  }

  onRunReport() {
    const value = this.productionDetailsForm.value;

    this.isRunning = true;
    this.isRunningChange.emit(this.isRunning);

    const effectiveFrom = this.effectiveDates?.[0]
      ? moment(this.effectiveDates?.[0]).startOf('date').toDate().toISOString()
      : undefined;
    const effectiveTo = this.effectiveDates?.[1]
      ? moment(this.effectiveDates?.[1]).endOf('date').toDate().toISOString()
      : undefined;
    const createdFrom = this.createdDates?.[0]
      ? moment(this.createdDates?.[0]).startOf('date').toDate().toISOString()
      : undefined;
    const createdTo = this.createdDates?.[1]
      ? moment(this.createdDates?.[1]).endOf('date').toDate().toISOString()
      : undefined;

    this.params = {
      commissionTeamIds: value.commissionTeams || [],
      leadSources: value.sources || [],
      statuses: value.policyStatuses || [],
      companyIds: value.companies || [],
      productTypeIds: value.productTypes || [],
      productIds: value.productNames || [],
      commissionOwners: value.commissionOwners || [],
      commissionOwnerIds: value.commissionOwners?.map(item => item.id) || [],
      commissionAgents: value.commissionAgents || [],
      commissionAgentIds: value.commissionAgents?.map(item => item.id) || [],
      groupBy: value.groupBy || '',
      visibleFields: this.exportSelectedFieldsList(),

      effectiveFrom,
      effectiveTo,
      createdFrom,
      createdTo
    };
    this.paramsChange.emit(this.params);
  }

  get sourcesSelectedCount(): number {
    const selectedItems = this.productionDetailsForm.get('sources')?.value || [];
    const leafItems = selectedItems.filter(item => item && item.leaf);
    return Array.isArray(leafItems) ? leafItems.length : 0;
  }

  toggleAllExportFields(isSelected: boolean) {
    this.isAllExportFieldsSelected = isSelected;
    if (this.isAllExportFieldsSelected) {
      this.exportSelectedFields.forEach(e => e.isSelected = true)
    } else {
      this.exportSelectedFields.forEach(e => {
        if (requiredExportFieldsProductionDetailsKey.includes(e.key)) {
          e.isSelected = true;
        } else {
          e.isSelected = false
        }
      })
    }
  }

  onExportFieldChanged() {
    this.isAllExportFieldsSelected =
      this.exportSelectedFields.length > 0 &&
      this.exportSelectedFields.every(e => e.isSelected === true)
    this.cdr.markForCheck()
  }

  get exportSelectedFieldsText(): string {
    return `Select fields (${this.isAllExportFieldsSelected ? 'All' : this.exportSelectedFieldsList().length})`
  }

  exportSelectedFieldsList() {
    return this.exportSelectedFields.filter(e => e.isSelected === true).map(e => e.key)
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
